// Offline struggle-verses bundle loader (share-scripture-found arc).
// This module is the boundary saved verse-results re-resolve through,
// parallel to the sibling topics.ts / stories.ts loaders: consumers never
// fetch /bible-search/struggle-verses.json directly.
//
// The bundle (public/bible-search/struggle-verses.json) is built offline
// by scripts/build-struggle-verses.mjs from the operator's curated
// docs/plans/curated_bible_verses source (rule #80 regen loop); the
// byte-identical server copy at api/_data/struggle-verses.json feeds the
// /api/grok-verse-suggest pipeline. It carries refs + operator context
// sentences only; verse TEXT resolves from bundled BSB at render (rule
// #62/#76). en-US content only at v1 (rule #7 carve-out class).
//
// A saved Prayer.verseResult stores `{ ref, category }` pairs (ids only,
// never prose; migration 032). `findBundleEntry` is the render-time
// lookup that turns a saved pair back into a full entry; a pair that no
// longer resolves (operator regen removed or re-reffed the entry) drops
// per the verse-result helper's self-heal (plan section 4).

// Must match BUNDLE_VERSION in scripts/build-struggle-verses.mjs. A
// future shape change bumps both; the loader rejects a bundle whose
// version it does not understand so the SAVED-verses layer fails closed
// here, and the verse-result helper falls back to the wire fetch (which
// carries everything needed to render) rather than showing false silence
// over a result that exists (plan section 4 step 2).
const SUPPORTED_BUNDLE_VERSION = 1;

const BUNDLE_URL = '/bible-search/struggle-verses.json';

/** One curated entry, the bundle-side mirror of verse-suggest.ts
 * SuggestedVerse minus `category` (the parent carries it). */
export interface StruggleVerseEntry {
  ref: string;
  book: string;
  bookSlug: string;
  chapter: number;
  segments: [number, number][] | null;
  wholeChapter: boolean;
  scale: 'short' | 'chapter';
  jesusWords: boolean;
  wjVerses: number[];
  context: string;
}

export interface StruggleVersesCategory {
  id: string;
  name: string;
  entries: StruggleVerseEntry[];
}

export interface StruggleVersesBundle {
  version: number;
  locale: string;
  categories: StruggleVersesCategory[];
}

let cache: Promise<StruggleVersesBundle> | null = null;

async function fetchBundle(): Promise<StruggleVersesBundle> {
  const res = await fetch(BUNDLE_URL);
  if (!res.ok) {
    throw new Error(`struggle-verses bundle fetch failed: ${res.status}`);
  }
  const data = (await res.json()) as StruggleVersesBundle;
  if (data.version !== SUPPORTED_BUNDLE_VERSION) {
    throw new Error(
      `unsupported struggle-verses bundle version ${data.version} (expected ${SUPPORTED_BUNDLE_VERSION})`,
    );
  }
  if (!Array.isArray(data.categories)) {
    throw new Error('struggle-verses bundle missing categories array');
  }
  return data;
}

/**
 * Lazy, module-cached bundle load (mirrors stories.ts / topics.ts: cache
 * the Promise, clear it on rejection so a transient failure does not
 * poison every subsequent call).
 */
export async function loadStruggleVerses(): Promise<StruggleVersesBundle> {
  if (!cache) {
    cache = fetchBundle().catch((err) => {
      cache = null;
      throw err;
    });
  }
  return cache;
}

/** Test/recovery hook: drop the cached bundle so the next load refetches. */
export function clearStruggleVersesCache(): void {
  cache = null;
}

/**
 * The (category, ref) lookup for saved verse-result entries. Linear scan
 * is fine at this scale (12 categories, ~500 entries total, saved
 * results cap out around 14 entries).
 */
export function findBundleEntry(
  bundle: StruggleVersesBundle,
  category: string,
  ref: string,
): StruggleVerseEntry | null {
  const cat = bundle.categories.find((c) => c.id === category);
  if (!cat) return null;
  return cat.entries.find((e) => e.ref === ref) ?? null;
}
